// Утилиты для работы с пользователями напрямую с файлом на сервере
// src/utils/userUtils.js

import { getApiUrl } from '../config/api';

/**
 * Получает список всех сохраненных пользователей из БД
 */
export const getLocalUsers = async () => {
    try {
        const response = await fetch('/api/files/users');
        if (response.ok) {
            const data = await response.json();
            return Array.isArray(data.users) ? data.users : [];
        }
        return [];
    } catch (error) {
        console.error('Ошибка получения пользователей:', error);
        return [];
    }
};

/**
 * Сохраняет пользователей напрямую на сервер через API
 */
export const saveUsersToFile = async (users) => {
    try {
        const response = await fetch(getApiUrl('api/files/users'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ users: users || [] })
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
        
        const result = await response.json();
        
        if (!result.success) {
            throw new Error(result?.error || 'Ошибка сохранения файла');
        }
        
        return { success: true };
    } catch (error) {
        console.error('Ошибка сохранения на сервер:', error);
        // НЕ скачиваем файл автоматически - показываем ошибку
        throw new Error(`Не удалось сохранить на сервер: ${error.message}. Убедитесь, что сервер запущен (npm run dev)`);
    }
};

/**
 * Получает информацию о текущем авторизованном сапе из localStorage
 */
const getCurrentSap = () => {
    try {
        const currentUser = localStorage.getItem('currentUser');
        if (currentUser) {
            const user = JSON.parse(currentUser);
            // Проверяем наличие обязательных полей
            if (user && user.id && user.username) {
            return {
                sapId: user.id,
                sapUsername: user.username
            };
            } else {
                console.warn('[getCurrentSap] currentUser не содержит id или username:', user);
            }
        }
    } catch (e) {
        console.error('Ошибка получения текущего сапа:', e);
    }
    return null;
};

/**
 * Сохраняет пользователя и обновляет файл users.json на сервере
 */
export const saveLocalUser = async (userData) => {
    try {
        console.log('[userUtils] saveLocalUser - received userData:', userData);
        console.log('[userUtils] saveLocalUser - generatedAmounts:', userData.generatedAmounts);
        
        // Загружаем текущих пользователей
        let users = await getLocalUsers();
        
        // Получаем информацию о текущем сапе
        const currentSap = getCurrentSap();
        
        const { mode, id, ...user } = userData;
        
        console.log('[userUtils] saveLocalUser - after destructuring, user.generatedAmounts:', user.generatedAmounts);
        
        // Генерируем ID для нового пользователя, если его нет
        const finalId = id || (mode === 'new' ? Date.now() + Math.random() : id);
        
        if (mode === 'new') {
            // Для нового пользователя сразу используем отдельный эндпоинт
            // НЕ добавляем в массив users, чтобы избежать массового сохранения
            const newUser = {
                ...user,
                id: finalId,
                checkpoint: 1,
                sentLog: {},
                timestamp: new Date().toISOString(),
                // Добавляем информацию о создателе (всегда пытаемся установить)
                ...(currentSap ? {
                    createdBy: currentSap.sapUsername,
                    createdBySapId: currentSap.sapId
                } : (() => {
                    console.warn('[saveLocalUser] Создание пользователя без авторизованного SAP. Поле createdBy не будет установлено.');
                    return {};
                })()),
                // Добавляем информацию о конфиге
                ...(user.configFile && {
                    configFile: user.configFile
                })
            };
            // НЕ добавляем в users - сразу сохраним через отдельный эндпоинт
        } else if (mode === 'update' || mode === 'replace') {
            // Обновляем существующего пользователя
            const index = users.findIndex(u => u.id === id);
            if (index !== -1) {
                // Сохраняем существующие checkpoint и sentLog если они не были изменены
                const existing = users[index];
                users[index] = {
                    ...existing,
                    ...user,
                    id: existing.id,
                    // Сохраняем оригинальный timestamp (дату регистрации), не перезаписываем
                    timestamp: existing.timestamp || user.timestamp || new Date().toISOString(),
                    // Сохраняем createdBy если его не было, иначе оставляем существующий
                    // Если createdBy отсутствует и есть текущий SAP - заполняем его
                    createdBy: existing.createdBy || (currentSap ? currentSap.sapUsername : null) || null,
                    createdBySapId: existing.createdBySapId || (currentSap ? currentSap.sapId : null) || null,
                    // Сохраняем configFile: строго сохраняем только изначальный конфиг (не перезаписываем и не устанавливаем при обновлениях)
                    configFile: existing.configFile !== undefined ? existing.configFile : null,
                    // Добавляем информацию о последнем обновлении
                    ...(currentSap && {
                        updatedBy: currentSap.sapUsername,
                        updatedBySapId: currentSap.sapId,
                        updatedAt: new Date().toISOString()
                    })
                };
            } else {
                // Если пользователь не найден, создаем нового
                const newUser = {
                    ...user,
                    id: id || Date.now() + Math.random(),
                    checkpoint: user.checkpoint || 1,
                    sentLog: user.sentLog || {},
                    timestamp: new Date().toISOString(),
                    // Добавляем информацию о создателе (всегда пытаемся установить)
                    ...(currentSap ? {
                        createdBy: currentSap.sapUsername,
                        createdBySapId: currentSap.sapId
                    } : (() => {
                        console.warn('[saveLocalUser] Создание пользователя без авторизованного SAP. Поле createdBy не будет установлено.');
                        return {};
                    })()),
                    // Добавляем информацию о конфиге
                    ...(user.configFile && {
                        configFile: user.configFile
                    })
                };
                users.push(newUser);
            }
        }
        
        // КРИТИЧЕСКОЕ ИСПРАВЛЕНИЕ: Всегда используем отдельный эндпоинт для сохранения одного пользователя
        // Это безопасно и не требует загрузки всех пользователей и массового сохранения
        if (finalId) {
            // Формируем payload для отправки
            let payloadToSend;
            
            if (mode === 'new') {
                // Для нового пользователя формируем полный объект
                payloadToSend = {
                    ...user,
                    id: finalId,
                    checkpoint: 1,
                    sentLog: {},
                    timestamp: new Date().toISOString(),
                    ...(currentSap ? {
                        createdBy: currentSap.sapUsername,
                        createdBySapId: currentSap.sapId
                    } : {}),
                    ...(user.configFile && { configFile: user.configFile })
                };
            } else if (mode === 'update' || mode === 'replace') {
                // Для обновления или замены используем данные из user
                // AdminPanel уже установил правильные checkpoint/sentLog в зависимости от изменений
                const existingUser = users.find(u => String(u.id) === String(finalId));
                payloadToSend = {
                    ...user,
                    id: finalId,
                    // Сохраняем оригинальный timestamp (дату регистрации) - AdminPanel уже установил его
                    timestamp: user.timestamp || existingUser?.timestamp || new Date().toISOString(),
                    // Используем checkpoint и sentLog из user (AdminPanel уже установил правильные значения)
                    checkpoint: user.checkpoint !== undefined ? user.checkpoint : (existingUser?.checkpoint || 1),
                    sentLog: user.sentLog !== undefined ? user.sentLog : (existingUser?.sentLog || {}),
                    // Сохраняем createdBy если он был у существующего пользователя (не перезаписываем)
                    createdBy: existingUser?.createdBy || (currentSap ? currentSap.sapUsername : null),
                    createdBySapId: existingUser?.createdBySapId || (currentSap ? currentSap.sapId : null),
                    // Сохраняем configFile если он был у существующего пользователя (не перезаписываем)
                    configFile: existingUser?.configFile || user.configFile || null,
                    // Добавляем информацию о последнем обновлении
                    ...(currentSap && {
                        updatedBy: currentSap.sapUsername,
                        updatedBySapId: currentSap.sapId,
                        updatedAt: new Date().toISOString()
                    })
                };
            } else {
                // Fallback для других режимов
                payloadToSend = {
                    ...user,
                    id: finalId,
                    timestamp: user.timestamp || new Date().toISOString()
                };
            }
            
            console.log('[userUtils] saveLocalUser - sending payload to server:', payloadToSend);
            console.log('[userUtils] saveLocalUser - payloadToSend.generatedAmounts:', payloadToSend.generatedAmounts);
            
            // Используем эндпоинт для сохранения/обновления одного пользователя
            try {
                const response = await fetch(getApiUrl(`api/files/users/${encodeURIComponent(finalId)}`), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payloadToSend)
                });
                
                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`HTTP ${response.status}: ${errorText}`);
                }
                
                const result = await response.json();
                
                if (!result.success) {
                    throw new Error(result?.error || 'Ошибка сохранения пользователя');
                }
                
                return { success: true, ...result };
            } catch (error) {
                console.error('Ошибка сохранения одного пользователя через отдельный эндпоинт:', error);
                // НЕ используем fallback на массовое сохранение - это опасно!
                // Лучше выбросить ошибку, чем рисковать потерей данных
                throw new Error(`Не удалось сохранить пользователя: ${error.message}. Попробуйте еще раз.`);
            }
        }
        
        // Этот код не должен выполняться, так как у нас всегда есть finalId
        // Но оставляем для совместимости на случай, если id не передан
        console.error('[userUtils] saveLocalUser - КРИТИЧЕСКАЯ ОШИБКА: сохранение без ID!');
        throw new Error('Не удалось сохранить пользователя: отсутствует ID');
    } catch (error) {
        console.error('Ошибка сохранения пользователя:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Скачивает файл users.json с пользователями (fallback метод)
 */
export const downloadUsersFile = (users) => {
    const data = { users: users || [] };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'users.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Удаляет пользователя через отдельный эндпоинт DELETE
 * БЕЗОПАСНО: не использует массовое сохранение
 */
export const deleteLocalUser = async (userId) => {
    try {
        console.log('[DELETE USER] Начало удаления, ID:', userId, 'Тип:', typeof userId);
        
        // Используем отдельный эндпоинт DELETE для удаления одного пользователя
        // Это безопасно и не требует массового сохранения
        const response = await fetch(getApiUrl(`api/files/users/${encodeURIComponent(userId)}`), {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            }
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
        
        const result = await response.json();
        
        if (!result.success) {
            throw new Error(result?.error || 'Ошибка удаления пользователя');
        }
        
        console.log('[DELETE USER] Пользователь успешно удален:', userId);
        return { success: true, ...result };
    } catch (error) {
        console.error('[DELETE USER] Ошибка удаления пользователя:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Экспортирует всех пользователей как JSON файл
 */
export const exportUsers = async () => {
    try {
        const users = await getLocalUsers();
        downloadUsersFile(users);
        return { success: true };
    } catch (error) {
        console.error('Ошибка экспорта пользователей:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Импортирует пользователей из файла и сохраняет на сервер
 */
export const importUsers = async (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = JSON.parse(e.target.result);
                const importedUsers = Array.isArray(data.users) ? data.users : (Array.isArray(data) ? data : []);
                
                if (!Array.isArray(importedUsers)) {
                    reject(new Error('Файл должен содержать массив пользователей'));
                    return;
                }
                
                // Сохраняем напрямую на сервер
                const result = await saveUsersToFile(importedUsers);
                
                resolve({ success: true, users: importedUsers, ...result });
            } catch (error) {
                reject(new Error('Неверный формат JSON файла'));
            }
        };
        reader.onerror = () => reject(new Error('Ошибка чтения файла'));
        reader.readAsText(file);
    });
};

